<script lang="ts">
    import {loadTranslations, changeLocale} from '$lib/i18n';
    import type {LayoutData} from './$types';
    import {page} from "$app/stores";
    import {userService} from "$lib/services/user.service";
    import {balances, OverlayLoaderShow, user, userReducer} from "$lib/stores";
    import {toasts} from "$lib/stores/toastStores";
    import {isConnected, messages} from "$lib/stores/websocket";
    import {initWebSocket} from "$lib/services/socket.service"; // Adjust path if needed
    import { browser } from "$app/environment";
    import { onMount } from 'svelte';

    // Use the traditional export let pattern for SvelteKit layouts
    export let data: LayoutData;

    let socket: any;
    let websocketCleanup: (() => void) | undefined;
    let userDataProcessed = false;

    // Initialize on mount
    onMount(async () => {
        // Change locale based on data
        changeLocale(data?.lang || 'en');

        // Load translations
        await loadTranslations(data?.lang || 'en', window.fetch);

        // Subscribe to page changes
        const unsubscribe = page.subscribe(value => {
            console.log(value);
            if (value.url.searchParams.get('r')) {
                localStorage.setItem('demo_referral', String(value.url.searchParams.get('r')));
            }
            if (value.url.searchParams.get('clickId')) {
                localStorage.setItem('f_ckid', String(value.url.searchParams.get('clickId')));
            }
            if (value.url.searchParams.get('campaignId')) {
                localStorage.setItem('f_cmpid', String(value.url.searchParams.get('campaignId')));
            }

            if (value.data?.user) {
                userReducer(value.data?.user);
                userService.getBalances().then((response: any) => {
                    if (response && response.data) {
                        return response.data;
                    }
                    toasts.send('Failed to fetch balances', 'error');
                    throw new Error('Failed to fetch balances');
                }).then(data => {
                    if (data && data.data) {
                        balances.set(data.data);
                    } else {
                        console.warn('No balance data available');
                        balances.set([]);
                    }

                    // Mark user data as processed and initialize WebSocket
                    userDataProcessed = true;
                    initializeWebSocket();
                }).catch(err => {
                    console.error('Error fetching balances:', err);
                    balances.set([]);

                    // Even if there's an error, mark as processed and try to initialize
                    userDataProcessed = true;
                    initializeWebSocket();
                });
            } else {
                // If there's no user data, still mark as processed after a delay
                // This handles the case where the user is not logged in
                setTimeout(() => {
                    userDataProcessed = true;
                    initializeWebSocket();
                }, 1000);
            }
        });

        // Initialize click tracking
        initClick();

        // Set overlay loader to false
        OverlayLoaderShow.set(false);

        // Clean up subscription and WebSocket on component destruction
        return () => {
            unsubscribe();
            if (websocketCleanup) {
                websocketCleanup();
            }
        };
    });

    // Function to initialize WebSocket only after user data is processed
    function initializeWebSocket() {
        if (!userDataProcessed || !browser) return;

        const socketUrl = import.meta.env.VITE_SOCKET_URL;

        if (socketUrl) {
            // Initialize WebSocket now that user data is processed
            websocketCleanup = initWebSocket(socketUrl);
        } else {
            console.error("WebSocket URL not found in environment variables");
        }
    }

    function initClick() {
        setTimeout(() => {
            if (!localStorage.getItem('ckt')) {
                userService.initClick({
                    referralCode: localStorage.getItem('demo_referral'),
                    token: uuid4(),
                }).then(() => {
                    console.log('Click initialized');
                });
            }
        }, 3000);
    }

    function uuid4() {
        let array = new Uint8Array(16);
        crypto.getRandomValues(array);

        // Manipulate the 9th byte
        array[8] &= 0b00111111; // Clear the first two bits
        array[8] |= 0b10000000; // Set the first two bits to 10

        // Manipulate the 7th byte
        array[6] &= 0b00001111; // Clear the first four bits
        array[6] |= 0b01000000; // Set the first four bits to 0100

        const pattern = "XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX";
        let idx = 0;

        return pattern.replace(
            /XX/g,
            () => array[idx++].toString(16).padStart(2, "0"), // padStart ensures a leading zero, if needed
        );
    }
</script>

<slot />
